import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { IPersonDataModel } from '../../data-models/person.data-model';
import { fetchCompanyPeople } from '../queries/MaggieCompanyQueries';
import { getTitlesForCompany } from '../../pages/CompanyProfiles/utils/peopleHelpers';

type PeopleValue = IPersonDataModel[] | null | Promise<IPersonDataModel[] | null>;

/**
 * The default fetches all people for the company. Treats `companyId <= 0`
 * (e.g. the `-1` "no company selected" sentinel) as invalid and returns
 * `null` without fetching — only positive ids are considered real companies.
 *
 * Consumers using `useAtomValue` will suspend on first read; callers needing a
 * non-suspending or sync peek should use {@link peopleByCompanyStateLoadableJ}.
 */
export const peopleByCompanyStateJ = atomFamily((companyId: number) =>
  atomWithDefault<PeopleValue>(() => {
    if (companyId <= 0) {
      return null;
    }
    return fetchCompanyPeople(companyId);
  })
);

export const peopleByCompanyStateLoadableJ = atomFamily((companyId: number) =>
  loadable(peopleByCompanyStateJ(companyId))
);

/**
 * Drives the global "Show hidden" toggle shared between the CompanyProfile
 * People tab and the Portfolio Overview People reporting grid.
 */
export const showHiddenPeopleAtomJ = atom<boolean>(false);

/** Shared across `SearchByName` and `usePeopleData`. */
export const nameSearchQueryAtomJ = atom<string>('');

/** Shared across `SearchByTitle` and `usePeopleData`. */
export const titleSearchQueryAtomJ = atom<string>('');

export function filterByNameAndTitle(
  people: IPersonDataModel[] | null,
  nameQuery: string,
  titleQuery: string,
  companyId: number
): IPersonDataModel[] | null {
  return (
    people?.filter((p) => {
      const titles = getTitlesForCompany(p, companyId);
      const hasTitleQuery =
        titleQuery.length === 0 || titles?.toLocaleLowerCase().includes(titleQuery.toLocaleLowerCase());
      const fullName = `${p.firstName} ${p.lastName}`;
      const hasNameQuery =
        nameQuery.length === 0 || fullName.toLocaleLowerCase().includes(nameQuery.toLocaleLowerCase());

      return hasTitleQuery && hasNameQuery;
    }) ?? null
  );
}
